import { Caption1, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useRef } from "react";
import { useDataTableStyles } from "@/components/data-table";
import { EmphasisValue } from "@/components/ui-typography";
import type { WeeklyTone } from "@/features/weekly-report/weekly-report-model";
import { StatusWord } from "./ink-report";

/**
 * 차트2의 경과별 준비.
 * 빗금은 같은 무리이고, 단색과 말은 기준에서 벗어난 막대에만 둔다.
 * 목록 칸의 짧은 막대는 같은 비율을 길이로 읽게 한다. 숫자는 옆에 맞춘다.
 */

const TICKS = [100, 75, 50, 25, 0];

export type ReadinessTone = "neutral" | "good" | "watch" | "risk";

export interface ReadinessColumn {
  key: string;
  name: string;
  /** 축 아래 짧은 경과. "착수 전", "40일". */
  elapsed: string;
  /** 준비. 0–100 */
  value: number;
  tone?: ReadinessTone;
  /** 막대 위의 한 마디. 주의·위험일 때만 막대가 단색이 된다. */
  callout?: string;
  /** 목록의 판정 단어. 차트는 쓰지 않는다. */
  verdict?: string;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function columnLabel(column: ReadinessColumn, value: number): string {
  return [column.name, column.elapsed, `${value}%`, column.callout].filter(Boolean).join(", ");
}

export function ReadinessChart({
  count,
  countLabel,
  standardLabel,
  averageLabel,
  columns,
  selected,
  onSelect,
  note,
}: {
  count: number;
  countLabel: string;
  standardLabel?: string;
  averageLabel?: string;
  columns: ReadinessColumn[];
  selected?: string;
  onSelect?: (key: string) => void;
  note?: string;
}) {
  const styles = useStyles();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const summary = columns.map((column) => columnLabel(column, clampPercent(column.value))).join(". ");

  return (
    <div className={styles.board}>
      <div className={styles.head}>
        <div className={styles.count}>
          <EmphasisValue className={styles.countNum}>{count}</EmphasisValue>
          <span className={styles.countLabel}>{countLabel}</span>
          {standardLabel ? <span className={styles.pill}>{standardLabel}</span> : null}
        </div>
        {averageLabel ? <Caption1 className={styles.quiet}>{averageLabel}</Caption1> : null}
      </div>
      <div className={styles.figure}>
        <div className={styles.yAxis} aria-hidden>
          {TICKS.map((tick) => (
            <span key={tick} className={styles.tick} style={{ top: `${100 - tick}%` }}>
              {tick}%
            </span>
          ))}
        </div>
        <div className={styles.plot}>
          <div className={styles.lines} aria-hidden>
            {TICKS.map((tick) => (
              <span key={tick} className={styles.gridLine} style={{ top: `${100 - tick}%` }} />
            ))}
          </div>
          <div className={styles.cols} role="listbox" aria-label={`경과별 준비. ${summary}`} style={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))` }}>
            {columns.map((column, index) => {
              const value = clampPercent(column.value);
              const tone = column.tone ?? "neutral";
              const on = selected === column.key;
              return (
                <button
                  key={column.key}
                  ref={(node) => {
                    refs.current[index] = node;
                  }}
                  type="button"
                  role="option"
                  aria-selected={on}
                  aria-label={columnLabel(column, value)}
                  tabIndex={on || (!selected && index === 0) ? 0 : -1}
                  className={mergeClasses(styles.col, on && styles.colOn)}
                  onClick={() => onSelect?.(column.key)}
                  onKeyDown={(event) => {
                    const dir = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
                    if (!dir) return;
                    event.preventDefault();
                    const next = Math.max(0, Math.min(columns.length - 1, index + dir));
                    onSelect?.(columns[next]?.key ?? column.key);
                    refs.current[next]?.focus();
                  }}
                >
                  <span
                    className={mergeClasses(styles.bar, tone === "watch" && styles.barWatch, tone === "risk" && styles.barRisk)}
                    style={{ height: `${value}%` }}
                  />
                  {column.callout ? (
                    <span className={mergeClasses(styles.callout, tone === "watch" && styles.calloutWatch, tone === "risk" && styles.calloutRisk)} style={{ bottom: `calc(${value}% + 4px)` }}>
                      {column.callout}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.nameRow} style={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))` }}>
          {columns.map((column) => {
            const on = selected === column.key;
            return (
              <span key={column.key} className={mergeClasses(styles.nameCell, on && styles.colOn)}>
                <Caption1 className={styles.elapsed}>{column.elapsed}</Caption1>
                <Tooltip content={column.name} relationship="description">
                  <span className={styles.name}>{column.name}</span>
                </Tooltip>
              </span>
            );
          })}
        </div>
      </div>
      {note ? <Caption1 className={styles.note}>{note}</Caption1> : null}
    </div>
  );
}

const VERDICT_TONE: Record<ReadinessTone, WeeklyTone> = {
  neutral: "neutral",
  good: "good",
  watch: "watch",
  risk: "risk",
};

/** 목록 칸의 짧은 준비 막대. 길이가 비율이고, 숫자는 오른쪽에 맞춘다. */
export function ReadinessMeter({ value, tone = "neutral" }: { value: number; tone?: ReadinessTone }) {
  const styles = useStyles();
  const width = clampPercent(value);
  return (
    <span className={styles.meter}>
      <span className={styles.track} aria-hidden>
        <span className={mergeClasses(styles.fill, tone === "good" && styles.fillGood, tone === "watch" && styles.fillWatch, tone === "risk" && styles.fillRisk)} style={{ width: `${width}%` }} />
      </span>
      <span className={styles.pct}>{width}%</span>
    </span>
  );
}

export function ReadinessList({
  title,
  rows,
  selected,
  onSelect,
}: {
  title: string;
  rows: ReadinessColumn[];
  selected?: string;
  onSelect?: (key: string) => void;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const refs = useRef<Array<HTMLTableRowElement | null>>([]);

  return (
    <div className={styles.board}>
      <p className={styles.listTitle}>{title}</p>
      <div className={styles.frame}>
        <table className={mergeClasses(table.table, styles.sheet)} aria-label={title}>
          <colgroup>
            <col style={{ width: "46%" }} />
            <col style={{ width: "28%" }} />
            <col style={{ width: "26%" }} />
          </colgroup>
          <thead>
            <tr>
              <th className={table.th} scope="col">
                플랜
              </th>
              <th className={mergeClasses(table.th, table.num)} scope="col">
                준비
              </th>
              <th className={table.th} scope="col">
                판정
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const tone = row.tone ?? "neutral";
              const on = selected === row.key;
              return (
                <tr
                  key={row.key}
                  ref={(node) => {
                    refs.current[index] = node;
                  }}
                  className={mergeClasses(onSelect && styles.rowPick, on && styles.rowOn)}
                  tabIndex={on || (!selected && index === 0) ? 0 : -1}
                  aria-selected={on}
                  onClick={() => onSelect?.(row.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect?.(row.key);
                      return;
                    }
                    const dir = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
                    if (!dir) return;
                    event.preventDefault();
                    const next = Math.max(0, Math.min(rows.length - 1, index + dir));
                    onSelect?.(rows[next]?.key ?? row.key);
                    refs.current[next]?.focus();
                  }}
                >
                  <th className={mergeClasses(table.td, styles.nameTd)} scope="row">
                    <span className={table.twoLine}>
                      <Tooltip content={row.name} relationship="description">
                        <span className={styles.clip}>{row.name}</span>
                      </Tooltip>
                      <Caption1 className={styles.quiet}>{row.elapsed}</Caption1>
                    </span>
                  </th>
                  <td className={mergeClasses(table.td, table.num)}>
                    <ReadinessMeter value={row.value} tone={tone} />
                  </td>
                  <td className={table.td}>
                    <StatusWord tone={VERDICT_TONE[tone]}>{row.verdict ?? "—"}</StatusWord>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const useStyles = makeStyles({
  board: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: "1px",
    borderRightWidth: "1px",
    borderBottomWidth: "1px",
    borderLeftWidth: "1px",
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusLarge,
  },
  head: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    minWidth: 0,
  },
  count: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    minWidth: 0,
    flexWrap: "wrap",
  },
  countNum: {
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
  },
  countLabel: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    whiteSpace: "nowrap",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    height: "22px",
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  figure: {
    display: "grid",
    gridTemplateColumns: "40px minmax(0, 1fr)",
    gridTemplateRows: "200px auto",
    minWidth: 0,
    paddingTop: "8px",
  },
  yAxis: {
    position: "relative",
    gridColumn: "1",
    gridRow: "1",
  },
  tick: {
    position: "absolute",
    right: "8px",
    transform: "translateY(-50%)",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  plot: {
    position: "relative",
    gridColumn: "2",
    gridRow: "1",
    minWidth: 0,
  },
  lines: {
    position: "absolute",
    top: "0",
    right: "0",
    bottom: "0",
    left: "0",
    pointerEvents: "none",
  },
  gridLine: {
    position: "absolute",
    left: "0",
    right: "0",
    height: "1px",
    transform: "translateY(-50%)",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  cols: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    height: "100%",
    minWidth: 0,
  },
  col: {
    position: "relative",
    height: "100%",
    minWidth: 0,
    padding: "0",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: "-2px",
    },
  },
  colOn: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  bar: {
    position: "absolute",
    left: "50%",
    bottom: "0",
    width: "36px",
    maxWidth: "72%",
    transform: "translateX(-50%)",
    borderTopLeftRadius: "3px",
    borderTopRightRadius: "3px",
    backgroundColor: tokens.colorNeutralForeground4,
    backgroundImage: `repeating-linear-gradient(-45deg, transparent 0 3px, ${tokens.colorNeutralBackground1} 3px 4px)`,
  },
  barWatch: {
    backgroundColor: tokens.colorPaletteDarkOrangeForeground1,
    backgroundImage: "none",
  },
  barRisk: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    backgroundImage: "none",
  },
  callout: {
    position: "absolute",
    left: "50%",
    zIndex: 2,
    transform: "translate(-50%, -100%)",
    paddingLeft: "6px",
    paddingRight: "6px",
    paddingTop: "1px",
    paddingBottom: "1px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: "1px",
    borderRightWidth: "1px",
    borderBottomWidth: "1px",
    borderLeftWidth: "1px",
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
  calloutWatch: { color: tokens.colorPaletteDarkOrangeForeground1 },
  calloutRisk: { color: tokens.colorPaletteRedForeground1 },
  nameRow: {
    display: "grid",
    gridColumn: "2",
    gridRow: "2",
    minWidth: 0,
  },
  nameCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
    minWidth: 0,
    paddingTop: "6px",
    paddingBottom: "2px",
    paddingLeft: "2px",
    paddingRight: "2px",
    textAlign: "center",
  },
  elapsed: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  name: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  listTitle: {
    margin: "0",
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  frame: {
    minWidth: 0,
    overflowX: "auto",
  },
  sheet: {
    minWidth: "420px",
  },
  nameTd: {
    height: "auto",
    minHeight: "34px",
    textAlign: "left",
    fontWeight: tokens.fontWeightRegular,
  },
  clip: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightRegular,
  },
  rowPick: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: "-2px",
    },
  },
  rowOn: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  meter: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    minWidth: "88px",
  },
  track: {
    width: "44px",
    height: "8px",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralBackground4,
    overflow: "hidden",
    flexShrink: 0,
  },
  fill: {
    display: "block",
    height: "100%",
    backgroundColor: tokens.colorNeutralForeground4,
  },
  fillGood: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
    backgroundImage: "none",
  },
  fillWatch: {
    backgroundColor: tokens.colorPaletteDarkOrangeForeground1,
    backgroundImage: "none",
  },
  fillRisk: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    backgroundImage: "none",
  },
  pct: {
    width: "36px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
  },
});
